import moment from "moment";
import {
  accessor,
  HIGH_MAX,
  HIGH_MIN,
  MODERATE_MAX,
  MODERATE_MIN,
  STABLE_MAX,
  STABLE_MIN,
} from "../constant/constant";
import { conditionCodes, matchSortKey } from "../data/data";

// check for active menu
export const isActive = (item, location) =>
  location.pathname.startsWith(item.path);

// check wheather in any profile page

/**
 * Creates FormData with proper handling of arrays and nested objects
 * @param {Object} options - The options object
 * @param {Object} options.data - The data object to convert to FormData
 * @param {File[]} options.files - Array of files to append
 * @param {string} options.fileKey - Key to use for file uploads (default: "files")
 * @param {string[]} options.arrayFields - Array of field names that should remain as arrays
 * @returns {FormData} The formatted FormData object
 */
export const createFormData = ({
  data,
  files,
  fileKey = "files",
  arrayFields = [],
}) => {
  const formData = new FormData();

  const appendFormData = (key, value) => {
    // Skip undefined values
    if (value === undefined) return;

    // Handle null values
    if (value === null) {
      formData.append(key, "");
      return;
    }

    // Handle Arrays
    if (Array.isArray(value)) {
      // If field is specified in arrayFields, append with brackets notation
      if (arrayFields.includes(key)) {
        value.forEach((item, index) => {
          if (typeof item === "object" && !Array.isArray(item)) {
            Object.entries(item).forEach(([itemKey, itemValue]) => {
              formData.append(`${key}[${index}][${itemKey}]`, itemValue);
            });
          } else {
            formData.append(`${key}[]`, item);
          }
        });
      } else {
        // Default array handling (converts to JSON string)
        formData.append(key, JSON.stringify(value));
      }
      return;
    }

    // Handle Objects
    if (value && typeof value === "object" && !(value instanceof File)) {
      formData.append(key, JSON.stringify(value));
      return;
    }

    // Handle primitive values and Files
    formData.append(key, value);
  };

  // Process all data fields
  Object.entries(data).forEach(([key, value]) => {
    appendFormData(key, value);
  });

  // Handle file uploads
  if (files?.length) {
    files.forEach((file) => {
      formData.append(fileKey, file);
    });
  }

  return formData;
};

export const isProfilePage = (path) => {
  const profilePagePath = [
    "/settings/profile",
    "/settings/profile/me",
    "/settings/change-password",
  ];
  return (
    profilePagePath.includes(path) || path.startsWith("/settings/profile/")
  );
};

// for only textfields
export const getChangedFields = ({ formData, dirtyFields }) => {
  const changedFields = {};
  Object.keys(dirtyFields).forEach((field) => {
    changedFields[field] = formData[field];
  });
  return changedFields;
};

export const setFormErrors = (errors, setError) => {
  if (errors && Object.keys(errors).length > 0) {
    Object.entries(errors).forEach(([field, messages]) => {
      if (Array.isArray(messages) && messages.length > 0) {
        setError(field, {
          type: "manual",
          message: messages[0], // Use the first error message
        });
      }
    });
  }
};

export const isError = (errors) => Object.keys(errors).length > 0;

export const compareArrays = (arr1, arr2) => {
  if (arr1.length !== arr2.length) return false; // Check if lengths are the same
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false; // Compare each element
  }
  return true;
};

export const createQueryParams = (paramsList) => {
  const searchParams = new URLSearchParams();
  Object.entries(paramsList).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      // searchParams.append(key, value);
      searchParams.append(
        key,
        typeof value === "object" ? JSON.stringify(value) : value
      );
    }
  });

  return searchParams.toString();
};

// sorting params

export const getSortValue = (
  id,
  direction,
  columnData,
  matchKeys = matchSortKey
) => {
  const column = columnData.find((item) => item?.id === id);
  if (!column) return "";

  const usersortKeys = [
    "created_by",
    "updated_by",
    "roles",
    "created_by_name",
    "aircraft_familiy",
    "engine_familiy",
    "msn_esn_number",
    "typical_pn",
    "pn",
    "pn_description",
    "pipeline_qty",
    "RANKING_CATEGORY",
  ];
  const apiField = usersortKeys.includes(column[accessor])
    ? matchKeys[column[accessor]]
    : column[accessor];
  return direction === "desc" ? `-${apiField}` : apiField;
};

export const dateFormat = (date, withTime = false) => {
  const formattedDate = date
    ? moment(date).isValid()
      ? withTime
        ? moment.utc(date).format("DD MMM YYYY hh:mm A")
        : moment.utc(date).format("DD MMM YYYY")
      : ""
    : "";
  return formattedDate;
};
export const dateFormatWithoutSec = (date) => {
  const formattedDate = moment.utc(date).format("DD MMM YYYY");
  // const time = moment.utc(date).format("hh:mm A");
  const time = moment.utc(date).format("hh:mm A");
  return <>{`${formattedDate} at ${time}`}</>;
};

// Remove special characters With Capitalize
export const capitalizeAndFormat = (text) => {
  if (typeof text !== "string") {
    return ""; // Return an empty string or handle it as needed
  }

  const words = text.split("\n").filter((word) => word.trim() !== "");

  const formattedText = words
    .map((word) =>
      word
        .replace(/_/g, " ")
        .trim()
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(" ")
    )
    .join("\n");

  return formattedText;
};

export const capitalizeAndFormatWithAcronym = (text) => {
  if (typeof text !== "string") {
    return "";
  }

  const words = text.split("\n").filter((word) => word.trim() !== "");

  const formattedText = words
    .map((word) =>
      word
        .replace(/_/g, " ")
        .trim()
        .split(" ")
        .map((w) =>
          w === w.toUpperCase()
            ? w
            : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
        )
        .join(" ")
    )
    .join("\n");

  return formattedText;
};

export const getCurrency = (num) => {
  if (num === 0 || (num && !isNaN(num) && isFinite(num))) {
    return Number(num)
      .toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      })
      .replace("$", "$ ");
  } else {
    return "$ 0.00";
  }
};

export const getNumberFormat = (num) => {
  if (typeof num === "number" && isFinite(num)) {
    return num.toLocaleString("en-US", { maximumFractionDigits: 2 });
  }
  if (typeof num === "string" && num.trim() !== "" && !isNaN(num)) {
    return Number(num).toLocaleString("en-US", { maximumFractionDigits: 2 });
  }
  if (typeof num === "string") {
    return num;
  }
  return "";
};

export const getLowerCase = (email) => {
  return String(email)?.toLocaleLowerCase();
};

export const getStringFromArray = (array) => {
  // Ensure we are working with a valid array
  if (Array.isArray(array) && array.length > 0) {
    return array.map((item) => item?.value).join(",");
  }
  return ""; // Return empty string if it's not a valid array
};

export const getSortedVisibleColumns = (columnData, visibleColumns) => {
  // Create a map of backend columns for easier lookup
  const backEndMap = Array.isArray(visibleColumns)
    ? visibleColumns.reduce((acc, col) => {
        acc[col.key_name] = col;
        return acc;
      }, {})
    : {};

  // Filter and sort columns based on backend visibility and order
  return columnData
    .filter((col) => {
      const backendCol = backEndMap[col.id];
      const isActive = backendCol && backendCol.active;
      const isVisible = col.isVisible !== false;

      // Always show structural columns (not user-toggleable)
      if (
        col.id === "expand" ||
        col.id === "select" ||
        col.id === "action" ||
        col.id === "row_icon"
      )
        return true;

      // Show columns that are active and visible
      return isActive && isVisible;
    })
    .sort((a, b) => {
      const structuralColumnOrder = {
        expand: 0,
        select: 1,
        row_icon: 2,
      };
      const orderA = structuralColumnOrder[a.id];
      const orderB = structuralColumnOrder[b.id];

      if (orderA !== undefined && orderB !== undefined) {
        return orderA - orderB;
      }
      if (orderA !== undefined) return -1;
      if (orderB !== undefined) return 1;

      // 'action' column always last
      if (a.id === "action") return 1;
      if (b.id === "action") return -1;

      // Check if both columns exist in backEndMap
      const backendOrderA = backEndMap[a.id] ? backEndMap[a.id]?.order : Infinity;
      const backendOrderB = backEndMap[b.id] ? backEndMap[b.id]?.order : Infinity;

      // Columns not in backEndMap will come last
      if (backendOrderA === Infinity && backendOrderB === Infinity) return 0;
      if (backendOrderA === Infinity) return 1;
      if (backendOrderB === Infinity) return -1;

      return backendOrderA - backendOrderB;
    });
};

export const getFormData = ({ data, files, fileKey = "files" }) => {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined) return;

    if (value && typeof value === "object") {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, value);
    }
  });
  if (files && files?.length) {
    files.forEach((file) => {
      formData.append(fileKey, file);
    });
  }

  return formData;
};

export const isValidJSON = (str) => {
  try {
    JSON.parse(str);
    return true;
  } catch (e) {
    return false;
  }
};

export const formatTimeTo12Hour = (time24) => {
  if (!time24) return "-";
  try {
    const [hours, minutes] = time24.split(":");
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  } catch (error) {
    return time24;
  }
};

// Helper function to validate and format dates
export const validateDates = {
  disablePastDates: (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  },
  disableFutureDates: (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  },
};

export const generateFieldsFromInputData = ({
  data,
  nameKey = "label",
  valueKey = "value",
  validation = [],
}) => {
  return data && data?.length
    ? data?.map((item) => ({
        label: item[nameKey] || "",
        key: item[nameKey] || "",
        fieldType: { type: "input" },
        alternateMatches: [`${item[nameKey]}`],
        example: item[valueKey] || 4300,
        validations: validation,
      }))
    : [];
};

export const findIndividualCashflowParam = (data = [], sectionKey) => {
  if (!Array.isArray(data)) {
    return null;
  }
  for (let item of data) {
    if (item.hasOwnProperty(sectionKey)) {
      return item[sectionKey];
    }
  }
  return null;
};

export const getFileNameFromPath = (path) => {
  return path?.split("/")[path?.split("/")?.length - 1];
};

export const convetDateToMonthString = (dateString) => {
  const date = new Date(dateString);
  const headerTitle =
    date.toLocaleString("default", { month: "short" }) +
    " " +
    date.getFullYear();
  return headerTitle;
};

export const transformFilterOptions = (filterDropdowns) => {
  const transformedOptions = {};

  Object.entries(filterDropdowns)?.forEach(([key, values]) => {
    transformedOptions[key] = values?.length
      ? values?.map((value) => ({
          label: key === "condition_code" ? getConditionLabel(value) : value,
          value,
          ...(key === "condition_code" && { chipLabel: value }),
        }))
      : [];
  });

  return transformedOptions;
};

export const toPascalCase = (str) => {
  return str
    ?.split("_")
    ?.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    ?.join(" ");
};

export const convertGraphRes = (graphsList) => {
  const graphs = Object.entries(graphsList).map(([title, graphsData]) => ({
    title,
    graphsData,
  }));
  return graphs;
};

export const normalizeString = (str) => str.replace(/\s+/g, "").toLowerCase();

export const matchedPermission = (allPemission = [], ids = []) => {
  return allPemission?.filter((item) => ids?.includes(item?.id));
};

export const isLocalhost = () => {
  const hostname = window.location.hostname;
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.startsWith("192.168.") || // local IP
    hostname.startsWith("10.") || // other common private IP range
    hostname.endsWith(".local") // optional: for mDNS/local domains
  );
};

export const getAPIUrl = () => {
  if (isLocalhost()) {
    return process.env.REACT_APP_API_BASE_URL;
  }
  return window.location.protocol === "https:"
    ? process.env.REACT_APP_API_BASE_URL
    : process.env.REACT_APP_API_BASE_URL_HTTP_ONLY;
};

export const roundToThreeDecimals = (num) => {
  const parsed = Number(num);
  if (isNaN(parsed)) {
    return num;
  }
  return Math.round(parsed * 100) / 100;
};

export const isEven = (num) => Number(num) % 2 === 0;

export const updateTableColumnData = ({
  data,
  rowIndex,
  columnId,
  value,
  updatedRow,
}) => {
  const list = data || [];
  const originalRow = list[rowIndex];
  if (!originalRow) return list;

  // Only the edited row gets a new reference so memoized rows can skip render
  const safe = updatedRow || {};
  const newRow = {};
  for (const key in originalRow) {
    newRow[key] = key in safe ? safe[key] : originalRow[key];
  }

  const updated = list.slice();
  updated[rowIndex] = { ...newRow, [columnId]: value };
  return updated;
};

/** Optimistic row update: show new value immediately, revert list on API failure. */
export const dispatchOptimisticPricingColumnUpdate = ({
  dispatch,
  currentList,
  rowIndex,
  columnId,
  value,
  rowOriginal,
  updatePricingData,
  updatePricingColumn,
  payload,
  onFulfilled,
}) => {
  const previousList = currentList || [];
  const optimisticList = updateTableColumnData({
    data: previousList,
    rowIndex,
    columnId,
    updatedRow: rowOriginal,
    value,
  });

  // Start the request first; the table re-render is deferred so it can't delay it
  const request = dispatch(updatePricingColumn(payload));
  queueMicrotask(() => dispatch(updatePricingData(optimisticList)));

  return request.then((result) => {
    if (result?.type?.endsWith("/fulfilled")) {
      const updatedList = updateTableColumnData({
        data: optimisticList,
        rowIndex,
        columnId,
        updatedRow: result?.payload?.data,
        value,
      });
      dispatch(updatePricingData(updatedList));
      onFulfilled?.();
    } else {
      dispatch(updatePricingData(previousList));
    }
  });
};

/** For hierarchical table data (`subRows`): TanStack Row `index` is flat (parent + expanded children), so it cannot index the top-level `data` array. Match by slug and merge into the correct node. */
export const updateNestedTableColumnDataBySlug = ({
  data,
  slug,
  columnId,
  value,
  updatedRow,
}) => {
  if (!slug || !Array.isArray(data)) {
    return data;
  }

  const safe =
    updatedRow && typeof updatedRow === "object" ? updatedRow : {};

  const mergeMatched = (originalRow) => {
    const newRow = {};
    for (const key in originalRow) {
      newRow[key] = Object.prototype.hasOwnProperty.call(safe, key)
        ? safe[key]
        : originalRow[key];
    }
    return {
      ...newRow,
      [columnId]: value,
    };
  };

  const walk = (rows) => {
    if (!rows?.length) {
      return rows;
    }
    let modified = false;
    const next = rows.map((row) => {
      if (row.slug === slug) {
        modified = true;
        const merged = mergeMatched(row);
        // A group row can share its slug with one of its own sub-rows
        if (Array.isArray(row.subRows) && row.subRows.length) {
          merged.subRows = walk(row.subRows);
        }
        return merged;
      }
      if (row.subRows?.length) {
        const subNext = walk(row.subRows);
        if (subNext !== row.subRows) {
          modified = true;
          return { ...row, subRows: subNext };
        }
      }
      return row;
    });
    return modified ? next : rows;
  };

  return walk(data);
};

// utils/buildPayload.js
export const buildPayloadForColumnEdit = ({ params, keys = [] }) => {
  const payload = {};

  keys.forEach((key) => {
    if (params.hasOwnProperty(key)) {
      const value = params[key];
      payload[key] =
        value === null || value === undefined || value === "" ? null : value;
    }
  });

  return payload;
};

const versionDateFormate = (date, withTime = false) => {
  const formattedDate = date
    ? moment(date).isValid()
      ? moment(date).format("DD MMM YYYY hh:mm A")
      : "-"
    : "-";
  return formattedDate;
};

export const generateVersionOptions = ({ params = [] }) => {
  const options = Array.isArray(params)
    ? params
        .filter(
          (item) =>
            item?.version_created_at &&
            item?.version_created_by &&
            typeof item.version_created_by.name === "string"
        )
        .map((item) => {
          const version = item.version ?? "0";
          const name = item.version_created_by.name;
          const date = versionDateFormate(item.version_created_at, true);

          const label = `v${version}${name ? ` - ${name}` : ""} - ${date}`;

          return {
            label,
            value: version,
          };
        })
    : [];
  return options;
};

export const showHistoricSwitch = (activeTabs) => [1, 3].includes(activeTabs);

export { showGroupedViewSwitch } from "../config/inventoryViewModeConfig";
export const getSubRowCount = (row) => {
  const subRows = row?.original?.subRows;
  const subRowCount = row?.original?.subRowCount;

  if (typeof subRowCount === "number" && !Number.isNaN(subRowCount)) {
    return subRowCount;
  }
  if (typeof subRows === "number" && !Number.isNaN(subRows)) {
    return subRows;
  }
  if (Array.isArray(subRows)) {
    return subRows.length;
  }
  return 0;
};

export const isSubRow = ({ row }) => row?.depth > 0;
export const isParentRow = ({ row }) => row?.depth === 0;
export const multiSubRow = ({ row }) => getSubRowCount(row) > 1;
export const parentRowWithmultiSubRow = ({ row }) =>
  isParentRow({ row }) && multiSubRow({ row });

export const addSortParam = ({ sortConfig, columns, paramsList }) => {
  if (sortConfig.sortBy && sortConfig.sortOrder) {
    const ordering = getSortValue(
      sortConfig.sortBy,
      sortConfig.sortOrder,
      columns
    );
    return {
      ...paramsList,
      ordering,
    };
  }
  return paramsList;
};

export const isNegative = (num) => {
  const n = Number(num); // Number(undefined) → NaN
  return Number.isFinite(n) && n < 0; // Number.isFinite(NaN) → false
};

export const isNegativeCurrency = (value) => {
  // Handle null, undefined, empty string
  if (value === null || value === undefined || value === "") {
    return false;
  }

  // If it's a string, remove currency formatting
  if (typeof value === "string") {
    // Remove currency symbols, spaces, commas, and percent signs
    const cleanedValue = value.replace(/[$,%\s]/g, "");

    // Convert to number
    const numValue = Number(cleanedValue);

    // Check if it's a valid number
    if (isNaN(numValue) || !isFinite(numValue)) {
      return false;
    }

    return numValue < 0;
  }

  // If it's already a number
  if (typeof value === "number") {
    // Check if it's a valid number
    if (isNaN(value) || !isFinite(value)) {
      return false;
    }

    return value < 0;
  }

  // For any other type, return false
  return false;
};

export const getConditionLabel = (condition) =>
  `${condition} ${conditionCodes[condition] ? `(${conditionCodes[condition]})` : ""}`;

export const resolveDateRange = (rangeValue) => {
  if (!rangeValue || rangeValue === "custom") return null;

  const today = moment();

  // ── Calendar-aware object: { unit: "months"|"years", amount: N } ──────────
  if (typeof rangeValue === "object" && rangeValue !== null) {
    const { unit, amount } = rangeValue;
    if (!unit || isNaN(amount)) return null;
    return {
      start_date: today.clone().subtract(amount, unit).format("YYYY-MM-DD"),
      end_date: today.format("YYYY-MM-DD"),
    };
  }

  // ── Day-count string: "30", "90" ──────────────────────────────────────────
  if (typeof rangeValue === "string") {
    const days = parseInt(rangeValue, 10);
    if (isNaN(days)) return null;
    return {
      start_date: today.clone().subtract(days, "days").format("YYYY-MM-DD"),
      end_date: today.format("YYYY-MM-DD"),
    };
  }

  return null;
};

export const is24MonthAvgRepairCost = (rowOrOriginal) => {
  const original = rowOrOriginal?.original ?? rowOrOriginal;
  return Boolean(original?.is_24_month_avg_repair_cost);
};

export const transformThresholds = (data) => {
  const result = {};

  data.forEach((item) => {
    const [min, max] = item.value_range.split("/");

    switch (item.threshold.toLowerCase()) {
      case "normal":
        result[STABLE_MIN] = min;
        result[STABLE_MAX] = max;
        break;

      case "moderate":
        result[MODERATE_MIN] = min;
        result[MODERATE_MAX] = max;
        break;

      case "high":
        result[HIGH_MIN] = min;
        result[HIGH_MAX] = max;
        break;

      default:
        break;
    }
  });

  return result;
};
